import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Field, Select } from '@/components/ui/field';
import { pruneParams } from '../helpers';
import {
  ASSIGNABLE_SCOPE_LEVELS,
  type Area,
  type AssignableScopeLevel,
  type Branch,
  type Floor,
  type ScopeSelection,
} from '../types';

/** The one field of `GET /brands` this picker needs — see `Pages/Brands/brandsApi.ts`. */
interface BrandOption {
  id: string;
  name: string;
}

interface Props {
  value: ScopeSelection;
  onChange: (next: ScopeSelection) => void;
  disabled?: boolean;
}

/**
 * P1-09 — the scope half of a role grant (§24.4).
 *
 * The level decides which entity picker is the *target*; the pickers above it
 * are navigation, because floors are only reachable through a branch and areas
 * only through a floor. Every step cascades: changing the branch clears the
 * floor and the area, changing the floor clears the area, so the selection can
 * never name a floor that does not sit in the chosen branch.
 *
 * TENANT carries no entity at all — `resolveScope()` rejects a TENANT grant
 * that arrives with a `scopeId`.
 *
 * ADR-0023 §3.1 — BRAND sits beside BRANCH, not above it in this picker's own
 * cascade: a brand is a tenant-wide grouping, not something reached by first
 * choosing a branch. It gets its own flat picker rather than joining the
 * branch/floor/area chain.
 */
export default function ScopePicker({ value, onChange, disabled = false }: Props) {
  const { t } = useTranslation();

  const needsBrand = value.level === 'BRAND';
  const needsBranch = value.level === 'BRANCH' || value.level === 'FLOOR' || value.level === 'AREA';
  const needsFloor = value.level === 'FLOOR' || value.level === 'AREA';
  const needsArea = value.level === 'AREA';

  const brandsQuery = useApi<unknown>(needsBrand ? '/brands' : null, { limit: 200 });
  const branchesQuery = useApi<unknown>(needsBranch ? '/branches' : null, { limit: 200 });
  const floorParams = useMemo(
    () => pruneParams({ limit: 200, branchId: value.branchId }),
    [value.branchId],
  );
  const floorsQuery = useApi<unknown>(needsFloor && value.branchId ? '/floors' : null, floorParams);
  const areaParams = useMemo(
    () => pruneParams({ limit: 200, floorId: value.floorId }),
    [value.floorId],
  );
  const areasQuery = useApi<unknown>(needsArea && value.floorId ? '/areas' : null, areaParams);

  // `GET /brands` answers `common/dto/pagination.dto.ts`'s `paginate()` shape
  // — `{ items, total, page, limit, pages }` — NOT the `{ data, meta }`
  // envelope `rowsOf()` unwraps (see `Pages/MenuGovernance/menuGovernanceApi.ts`
  // for the same gotcha on the same envelope). Reading `.data` here would
  // silently show zero brands rather than erroring.
  const brands = (brandsQuery.data as { items?: BrandOption[] } | null)?.items ?? [];
  const branches = rowsOf<Branch>(branchesQuery.data).rows;
  const floors = rowsOf<Floor>(floorsQuery.data).rows;
  const areas = rowsOf<Area>(areasQuery.data).rows;

  const setLevel = (level: AssignableScopeLevel) =>
    onChange({
      level,
      brandId: level === 'BRAND' ? value.brandId : '',
      branchId: level === 'BRANCH' || level === 'FLOOR' || level === 'AREA' ? value.branchId : '',
      floorId: level === 'FLOOR' || level === 'AREA' ? value.floorId : '',
      areaId: level === 'AREA' ? value.areaId : '',
    });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t('settings.scope.levelLabel')} required>
        <Select
          value={value.level}
          disabled={disabled}
          onChange={(e) => setLevel(e.target.value as AssignableScopeLevel)}
        >
          {ASSIGNABLE_SCOPE_LEVELS.map((level) => (
            <option key={level} value={level}>
              {t(`settings.scope.level.${level}`)}
            </option>
          ))}
        </Select>
      </Field>

      {needsBrand ? (
        <Field label={t('settings.scope.brand')} required>
          <Select
            value={value.brandId}
            disabled={disabled || brandsQuery.loading}
            onChange={(e) => onChange({ ...value, brandId: e.target.value })}
          >
            <option value="">
              {brandsQuery.loading ? t('common.loading') : t('settings.scope.selectBrand')}
            </option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : !needsBranch ? (
        <div className="flex items-end">
          <p className="pb-3 text-xs text-ink-muted">
            {t('settings.scope.tenantHint')}
          </p>
        </div>
      ) : (
        <Field
          label={t('settings.scope.branch')}
          required
          hint={value.level === 'BRANCH' ? undefined : t('settings.scope.branchNarrowHint')}
        >
          <Select
            value={value.branchId}
            disabled={disabled || branchesQuery.loading}
            onChange={(e) =>
              onChange({ ...value, branchId: e.target.value, floorId: '', areaId: '' })
            }
          >
            <option value="">
              {branchesQuery.loading ? t('common.loading') : t('settings.scope.selectBranch')}
            </option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {needsFloor && (
        <Field
          label={t('settings.scope.floor')}
          required
          hint={!value.branchId ? t('settings.scope.pickBranchFirst') : undefined}
        >
          <Select
            value={value.floorId}
            disabled={disabled || !value.branchId || floorsQuery.loading}
            onChange={(e) => onChange({ ...value, floorId: e.target.value, areaId: '' })}
          >
            <option value="">
              {floorsQuery.loading ? t('common.loading') : t('settings.scope.selectFloor')}
            </option>
            {floors.map((floor) => (
              <option key={floor.id} value={floor.id}>
                {floor.name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {needsArea && (
        <Field
          label={t('settings.scope.area')}
          required
          hint={!value.floorId ? t('settings.scope.pickFloorFirst') : undefined}
        >
          <Select
            value={value.areaId}
            disabled={disabled || !value.floorId || areasQuery.loading}
            onChange={(e) => onChange({ ...value, areaId: e.target.value })}
          >
            <option value="">
              {areasQuery.loading ? t('common.loading') : t('settings.scope.selectArea')}
            </option>
            {areas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </div>
  );
}
